<template>
  <div>
    <ProgressBar
      v-if="!isLiteView()"
      :active-step="pageTitle"
    />
    <PageHeading
      :default-heading="pageTitle"
      :edit-heading="pageTitle"
    />
    <form
      novalidate="novalidate"
      @submit.prevent
    >
      <div class="pvd-container container-divider">
        <h2 class="pvd-heading-L">
          What's their name?
        </h2>
        <apx-full-name
          id="fullName"
          group-label="Name"
          :first-name="firstName"
          first-name-label="First"
          :middle-name="middleName"
          middle-name-label="Middle"
          :last-name="lastName"
          last-name-label="Last"
          required
          @value-changed="fullNameChanged"
        />
      </div>
      <div class="pvd-container container-divider">
        <h2 class="pvd-heading-L">
          Select any authorities granted by the trust agreement
        </h2>
        <div
          class="pvd-form-row"
          role="group"
          aria-labelledby="checkgroup-label"
        >
          <div class="form-row-label form-row-label-top label-icon">
            <label
              id="checkgroup-label"
              class="pvd-label"
            >
              <a
                id="trust-authorities-modal-trigger"
                href="javascript:void(0);"
              >
                <pvd-icon
                  icon="help"
                  class="m-small-left"
                />
              </a> Authorities
            </label>
          </div>
          <pvd-modal
            id="trust-authorities-modal"
            for="trust-authorities-modal-trigger"
            class="small"
          >
            <div slot="modal-header">
              <h2 class="pvd-heading-L">
                Trust authorities
              </h2>
            </div>
            <div slot="modal-body">
              <div class="pvd-container">
                <p>
                  Trust authorities include the power to amend conditions of the trust,
                  revoke conditions of the trust, and the power to appoint or remove
                  individual trustees. The people who may hold these authorities can
                  vary by type of trust and the specific trust terms.
                  <br>
                  <br>
                  <strong>Refer to your trust documents</strong>
                  for this information when answering this question.
                </p>
              </div>
            </div>
          </pvd-modal>
          <div class="form-row-content">
            <fieldset aria-labelledby="trust-role--label">
              <div class="pvd-checkbox">
                <input
                  id="authorities--amend"
                  v-model="amendValue"
                  v-validate="{required: appointRemoveValue || revokeValue?false:true}"
                  type="checkbox"
                  name="authorities"
                  value="amend conditions"
                >
                <label for="authorities--amend">
                  <span
                    class="checkmark"
                    aria-hidden="true"
                  >
                    <pvd-icon icon="checkmark" />
                  </span>
                  Can amend conditions of the trust
                </label>
              </div>
              <div
                v-if="acc_details.isIrrevocable === false"
                class="pvd-checkbox"
              >
                <input
                  id="authorities--revoke"
                  v-model="revokeValue"
                  v-validate="{required: appointRemoveValue || amendValue?false:true}"
                  type="checkbox"
                  name="authorities"
                  value="revoke conditions"
                >
                <label for="authorities--revoke">
                  <span
                    class="checkmark"
                    aria-hidden="true"
                  >
                    <pvd-icon icon="checkmark" />
                  </span>
                  Can revoke conditions of the trust
                </label>
              </div>
              <div class="pvd-checkbox">
                <input
                  id="authorities--appoint-remove"
                  v-model="appointRemoveValue"
                  v-validate="{required: amendRevokeValue?false:true}"
                  type="checkbox"
                  name="authorities"
                  value="appoint/remove trustees"
                >
                <label for="authorities--appoint-remove">
                  <span
                    class="checkmark"
                    aria-hidden="true"
                  >
                    <pvd-icon icon="checkmark" />
                  </span>
                  Can appoint/remove trustees
                </label>
              </div>
            </fieldset>
          </div>
          <InlineMessage
            v-if="errors.has('authorities')"
            message="Please make a selection."
            :css-class="'form-row-message p-small-top'"
          />
          <InlineMessage
            v-if="shouldDisplayErrorMSG"
            :message="shouldDisplayErrorMSG"
            css-class="form-row-message p-small-topm"
          />
        </div>
      </div>
      <div class="pvd-container container-divider">
        <h2 class="pvd-heading-L">
          Enter their personal information details
        </h2>
        <div>
          <apx-ssn
            id="ssn"
            class="authority-ssn"
            label="Social Security number"
            :value="ssn"
            required
            :validation-rules="buildTrustSSNRules()"
            @value-changed="ssnChanged"
          />
          <div class="pvd-form-row top-margin-zero">
            <div class="form-row-label">
              &nbsp;
            </div>
            <div class="form-row-content">
              &nbsp;
            </div>
            <InlineMessage
              v-if="ssnMatchesExistingSSN"
              message="This SSN has already been entered for someone else.
                Please enter a valid SSN for this person, or review and correct
                the entries you have already made."
              css-class="form-row-message p-small-top"
            />
          </div>
          <apx-date-select
            id="dateOfBirth"
            group-label="Date of birth"
            :day-value="dobDay"
            :month-value="dobMonth"
            :year-value="dobYear"
            validation-text="The date of birth entered is not a valid date."
            default-select-label="Select"
            min-date="01/01/1900"
            eighteen-or-above
            eighteen-or-above-error="This person must be at least 18 years old."
            required
            @value-changed="dateOfBirthChanged"
          />
          <div class="pvd-form-row top-margin-zero">
            <div class="form-row-label">
              &nbsp;
            </div>
            <div class="form-row-content">
              &nbsp;
            </div>
          </div>
          <apx-select
            id="authorityCountryList"
            label="Country of citizenship"
            error-text="Select a country of citizenship."
            :options="countryList"
            :value="residenceCountry"
            required
            @value-changed="residenceCountryChanged"
          />
        </div>
      </div>
      <div
        class="pvd-container container-divider"
      >
        <h2 class="pvd-heading-L">
          What's their legal residential address?
        </h2>
        <apx-address
          id="mailingAddress"
          id-prefix="authority-legal-residential-address"
          :address-line1="mailingAddress.line1"
          :address-line2="mailingAddress.line2"
          :city-name="mailingAddress.city"
          :state-code="mailingAddress.state"
          :zip-code="mailingAddress.zip"
          prevent-pobox
          required
          @value-changed="addressChanged('mailing', $event)"
        />
      </div>

      <PageNavigation
        primary-button-type="button"
        :show-primary-button-spinner="shouldShowLoadingSpinner"
        :disable-primary-button="shouldDisableNext"
        :primary-button-label="activePrimaryButtonLabel"
        secondary-button-label="Cancel"
        :page-exit-url="pd_content.overlay_exit_link.overview_url"
        @primary-button-clicked="nextButtonClicked"
        @secondary-button-clicked="cancelButtonClicked"
      />
    </form>
    <div
      id="fs-steps"
      class="personal-info-page"
      data-operation="IdentifyUser"
      data-step="Init"
      style="display: none"
    >
      &nbsp;
    </div>
  </div>
</template>

<script>
import ProgressBar from '../components/ProgressBar.vue';
import PageNavigation from '../components/PageNavigation.vue';
import PageHeading from '../components/PageHeading.vue';
import dataHelper from '../mixins/dataHelper';
import dataSanitize from '../mixins/dataSanitize';
import measurement from '../mixins/measurement';
import utilityFunctions from '../mixins/utility';
import validationHelper from '../mixins/validationHelper';
import widgetHelper from '../mixins/widgetHelper';
import pageNavigationHelper from '../mixins/pageNavigationHelper';
import trustHelper from '../mixins/trustHelper';
import addressAutocomplete from '../mixins/addressAutocomplete';
import InlineMessage from '../components/InlineMessage.vue';

export default {
  name: 'TrustAuthority',
  components: {
    ProgressBar,
    PageNavigation,
    PageHeading,
    InlineMessage,
  },
  mixins: [
    dataHelper,
    dataSanitize,
    measurement,
    utilityFunctions,
    validationHelper,
    widgetHelper,
    pageNavigationHelper,
    trustHelper,
    addressAutocomplete,
  ],
  data() {
    return {
      backButtonAttempt: false,
      mailingAddress: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
      },
      ssn: '',
      firstName: '',
      middleName: '',
      lastName: '',
      dateOfBirth: '',
      residenceCountry: '',
      hasAppointRemove: false,
      hasAmendRevoke: false,
      hasAmend: false,
      hasRevoke: false,
      addingAuthorityOnlyPersons: undefined,
      primaryButtonLabel: 'Save',
      MAX_APPOINT_REMOVE: 6,
      MAX_AMEND_REVOKE: 6,
      additionalRoles: [],
      applicant: {},
      isLoading: false,
      pageTitle: 'Tell us about the person you\'re adding',
    };
  },
  computed: {
    shouldDisableNext() {
      return this.isLoading ? 'Y' : 'N';
    },
    shouldDisplayErrorMSG() {
      if (!this.appointRemoveValid && !this.amendRevokeValid) {
        return this.pd_content.authority.amend_appoint;
      }
      if (!this.appointRemoveValid) {
        return this.pd_content.authority.appoint;
      }
      if (!this.amendRevokeValid) {
        return this.pd_content.authority.amend;
      }
      return false;
    },
    shouldShowLoadingSpinner() {
      return this.isLoading ? 'Y' : 'N';
    },
    activePrimaryButtonLabel() {
      return this.isLoading ? this.pd_content.primary_button.wait : this.primaryButtonLabel;
    },
    ssnMatchesExistingSSN: {
      get() {
        const existingSSNs = [
          ...this.additionalRoles.filter((user, index) => index !== this.additionalUserIndex)
            .map((user) => user.ssn),
          this.applicant.ssn,
        ];
        return existingSSNs.includes(this.ssn);
      },
    },
    appointRemoveValid() {
      if (this.hasAppointRemove && this.hasMaxAppointRemove) return false;
      return true;
    },
    amendRevokeValid() {
      if (this.hasAmendRevoke && this.hasMaxAmendRevoke) return false;
      return true;
    },
    appointRemoveValue: {
      get() {
        return this.hasAppointRemove;
      },
      set(newHasAppointRemove) {
        this.hasAppointRemove = newHasAppointRemove;
        this.commitAdditionalUserData(this.hasAppointRemove, 'hasAppointRemove', this.additionalUserIndex);
      },
    },
    amendRevokeValue: {
      get() {
        return this.hasAmendRevoke;
      },
      set(newHasAmendRevoke) {
        this.hasAmendRevoke = newHasAmendRevoke;
        this.commitAdditionalUserData(this.hasAmendRevoke, 'hasAmendRevoke', this.additionalUserIndex);
      },
    },
    amendValue: {
      get() {
        return this.hasAmend;
      },
      set(newHasAmend) {
        this.hasAmend = newHasAmend;
        this.amendRevokeValue = (this.hasAmend || this.hasRevoke);
        this.commitAdditionalUserData(this.hasAmend, 'hasAmend', this.additionalUserIndex);
      },
    },
    revokeValue: {
      get() {
        return this.hasRevoke;
      },
      set(newHasRevoke) {
        this.hasRevoke = newHasRevoke;
        this.amendRevokeValue = (this.hasRevoke || this.hasAmend);
        this.commitAdditionalUserData(this.hasRevoke, 'hasRevoke', this.additionalUserIndex);
      },
    },
    hasMaxAppointRemove() {
      if (this.pu_additionalDetails) {
        const applicantHasAppointRemove = this.pu_additionalDetails.hasAppointRemove ? 1 : 0;
        return this.additionalRoles.filter((person) => person.hasAppointRemove).length
          + applicantHasAppointRemove >= this.MAX_APPOINT_REMOVE;
      }

      return false;
    },
    hasMaxAmendRevoke() {
      if (this.pu_additionalDetails) {
        const applicantHasAmendRevoke = this.pu_additionalDetails.hasAmendRevoke ? 1 : 0;
        return this.additionalRoles.filter((person) => person.hasAmendRevoke).length
          + applicantHasAmendRevoke >= this.MAX_AMEND_REVOKE;
      }

      return false;
    },
    dobMonth() {
      return this.dateOfBirth ? this.dateOfBirth.split('/')[0] : '';
    },
    dobDay() {
      return this.dateOfBirth ? this.dateOfBirth.split('/')[1] : '';
    },
    dobYear() {
      return this.dateOfBirth ? this.dateOfBirth.split('/')[2] : '';
    },
    countryList: {
      get() {
        return JSON.stringify(this.getCountryList());
      },
    },
  },
  mounted() {
    this.configureBackButtonFunctionality(() => this.$emit('invoke-previous'));
    this.removeModalClassIfExists();

    document.title = this.setTitle(this.pageTitle);

    this.populateCountryList('#authorityCountryList');
    this.configureBackButtonFunctionality(() => this.$emit('invoke-previous'));
    this.initGoogleAutocomplete('authority-legal-residential-address-addressLine1', this.trustFillAddressValues.bind(null, 'authority-legal-residential-address', this.addressChanged.bind(this, 'mailing')));

    this.applicant = this.getInstanceData().formData.primaryUser;
    this.additionalRoles = this.getInstanceData().formData.additionalUsers;

    this.isLoading = false;

    const instanceData = this.getInstanceData();

    let userIndex;

    if (instanceData && instanceData.currentState) {
      userIndex = instanceData.currentState.userIndex;
    }

    if (typeof userIndex !== 'undefined' && Number.isInteger(userIndex)) {
      this.additionalUserIndex = userIndex;
      this.currentUser = { ...instanceData.formData.additionalUsers[this.additionalUserIndex] };
      this.primaryButtonLabel = 'Update';
      this.$store.commit('currentState/updateField', { path: 'userIndex', value: '' });

      // Repopulate form for current user
      if (this.currentUser.firstName) { this.firstName = this.currentUser.firstName; }
      if (this.currentUser.middleName) { this.middleName = this.currentUser.middleName; }
      if (this.currentUser.lastName) { this.lastName = this.currentUser.lastName; }
      if (this.currentUser.hasAppointRemove) { this.hasAppointRemove = true; }
      if (this.currentUser) {
        this.amendValue = !!this.currentUser.hasAmend;
        this.revokeValue = !!this.currentUser.hasRevoke;
      }
      if (this.currentUser.addresses && this.currentUser.addresses.mailing) { this.mailingAddress = this.currentUser.addresses.mailing; }
      if (this.currentUser.dob) { this.dateOfBirth = this.currentUser.dob; }
      if (this.currentUser.ssn) { this.ssn = this.currentUser.ssn; }
      if (this.currentUser.residenceCountry) { this.residenceCountry = this.currentUser.residenceCountry; }
    } else {
      this.additionalUserIndex = instanceData.formData.additionalUsers.length;

      this.commitAdditionalUserData(false, 'hasAmendRevoke', this.additionalUserIndex);
      this.commitAdditionalUserData(false, 'hasAppointRemove', this.additionalUserIndex);
      this.commitAdditionalUserData('authority', 'role', this.additionalUserIndex);
    }
  },

  methods: {
    nextButtonClicked(navAction) {
      this.isLoading = true;
      this.performPageValidation(navAction).then((valid) => {
        if (
          valid
          && this.appointRemoveValid
          && this.amendRevokeValid
          && !this.ssnMatchesExistingSSN
        ) {
          this.performNavigation(this.pd_content.primary_button.value);
        } else {
          this.isLoading = false;
        }
      });
    },
    cancelButtonClicked() {
      if (this.currentUser) {
        // Edit - we need to delete the changes but not the user
        const userReset = [...this.getInstanceData().formData.additionalUsers];
        userReset[this.additionalUserIndex] = this.currentUser;
        this.setListInstanceData('formData', userReset, 'additionalUsers');
      } else {
        // New - we need to delete the user entirely
        this.setListInstanceData('formData', this.getInstanceData().formData.additionalUsers.slice(0, -1), 'additionalUsers');
      }
    },
    addingAuthorityOnlyPersonsChanged(event) {
      this.addingAuthorityOnlyPersons = event;
    },
    fullNameChanged(event) {
      const index = this.additionalUserIndex;
      this.firstName = event.detail.fullname.firstName;
      this.middleName = event.detail.fullname.middleName;
      this.lastName = event.detail.fullname.lastName;
      this.commitAdditionalUserData(this.firstName, 'firstName', index);
      this.commitAdditionalUserData(this.middleName, 'middleName', index);
      this.commitAdditionalUserData(this.lastName, 'lastName', index);
    },
    ssnChanged(event) {
      this.ssn = event.detail.value;
      this.commitAdditionalUserData(this.ssn, 'ssn', this.additionalUserIndex);
    },
    dateOfBirthChanged(event) {
      this.dateOfBirth = `${event.detail.month}/${event.detail.day}/${event.detail.year}`;
      this.commitAdditionalUserData(this.dateOfBirth, 'dob', this.additionalUserIndex);
    },
    residenceCountryChanged(event) {
      this.residenceCountry = event.detail.selectValue;
      this.commitAdditionalUserData(this.residenceCountry, 'residenceCountry', this.additionalUserIndex);
    },
    addressChanged(addressType, event) {
      const index = this.additionalUserIndex;

      this[`${addressType}Address`] = {
        line1: event.detail.address.addressLine1,
        line2: event.detail.address.addressLine2,
        city: event.detail.address.cityName,
        state: event.detail.address.stateCode,
        zip: event.detail.address.zipCode,
      };

      const addresses = {
        ...(this.getInstanceData().formData.additionalUsers[this.additionalUserIndex].addresses || {}),
        [addressType]: {
          line1: event.detail.address.addressLine1,
          line2: event.detail.address.addressLine2,
          city: event.detail.address.cityName,
          state: event.detail.address.stateCode,
          zip: event.detail.address.zipCode,
        },
      };

      this.commitAdditionalUserData(addresses, 'addresses', index);
    },
  },
};

</script>

<style scoped>
  .top-margin-zero {
    margin-top: 0;
  }
  .mt-1{
    margin-top: 1rem;
  }
</style>
